/**
 * Secure Memory Utilities
 * Provides secure memory management for sensitive data in the browser
 */

/**
 * Securely clear a string from memory
 * Note: JavaScript strings are immutable, so this is best-effort
 * @param {string} str - The string to clear
 */
function getWebCrypto() {
    if (typeof globalThis !== 'undefined' && globalThis.crypto && typeof globalThis.crypto.getRandomValues === 'function') {
        return globalThis.crypto;
    }
    return null;
}

export function secureClearString(str) {
    if (typeof str !== 'string') return;
    
    try {
        const webCrypto = getWebCrypto();
        if (!webCrypto) {
            throw new Error('Secure randomness unavailable: crypto.getRandomValues is not accessible');
        }

        // Overwrite the string with random data multiple times
        // This is best-effort since JS strings are immutable
        const randomData = webCrypto.getRandomValues(new Uint8Array(str.length));
        const randomString = Array.from(randomData, byte => String.fromCharCode(byte)).join('');
        
        // Attempt to overwrite by creating new references
        str = null;
        return randomString; // Return random data to potentially overwrite memory
    } catch (error) {
        console.warn('Failed to securely clear string:', error);
        return null;
    }
}

/**
 * Securely clear an object containing sensitive data
 * @param {Object} obj - Object to clear
 */
export function secureClearObject(obj) {
    if (!obj || typeof obj !== 'object') return;
    
    try {
        // Recursively clear string properties
        for (const key in obj) {
            if (obj.hasOwnProperty(key)) {
                if (typeof obj[key] === 'string') {
                    obj[key] = secureClearString(obj[key]);
                } else if (typeof obj[key] === 'object' && obj[key] !== null) {
                    secureClearObject(obj[key]);
                }
                delete obj[key];
            }
        }
    } catch (error) {
        console.warn('Failed to securely clear object:', error);
    }
}

/**
 * Securely clear an array containing sensitive data
 * @param {Array} arr - Array to clear
 */
export function secureClearArray(arr) {
    if (!Array.isArray(arr)) return;
    
    try {
        // Clear each element
        for (let i = 0; i < arr.length; i++) {
            if (typeof arr[i] === 'string') {
                arr[i] = secureClearString(arr[i]);
            } else if (typeof arr[i] === 'object' && arr[i] !== null) {
                secureClearObject(arr[i]);
            }
            arr[i] = null;
        }
        
        // Clear the array itself
        arr.length = 0;
    } catch (error) {
        console.warn('Failed to securely clear array:', error);
    }
}

/**
 * Create a secure deep copy of an object without using JSON serialization
 * This avoids creating intermediate string representations of sensitive data
 * @param {any} obj - Object to copy
 * @returns {any} Deep copy of the object
 */
function secureDeepCopy(obj) {
    if (obj === null || typeof obj !== 'object') {
        return obj;
    }
    
    if (Array.isArray(obj)) {
        return obj.map(item => secureDeepCopy(item));
    }
    
    const copy = {};
    for (const key in obj) {
        if (obj.hasOwnProperty(key)) {
            copy[key] = secureDeepCopy(obj[key]);
        }
    }
    return copy;
}

/**
 * Securely overwrite memory with random data
 * @param {number} size - Number of random bytes to generate
 * @returns {Uint8Array} Random data to help overwrite memory
 */
function generateRandomOverwrite(size) {
    try {
        const webCrypto = getWebCrypto();
        if (!webCrypto) {
            throw new Error('Secure randomness unavailable: crypto.getRandomValues is not accessible');
        }

        return webCrypto.getRandomValues(new Uint8Array(size));
    } catch (error) {
        // As a last resort, do not use Math.random. Surface an explicit error
        // to prevent insecure randomness from being used silently.
        throw new Error('Secure randomness unavailable: crypto.getRandomValues is not accessible');
    }
}

/**
 * Create a secure wrapper for sensitive data that auto-clears on destruction
 * @param {any} data - Sensitive data to wrap
 * @returns {Object} Wrapper with clear() method
 */
export function createSecureWrapper(data) {
    // Create a secure deep copy without JSON serialization
    let sensitiveData = secureDeepCopy(data);
    
    return {
        get: () => sensitiveData,
        clear: () => {
            if (typeof sensitiveData === 'string') {
                sensitiveData = secureClearString(sensitiveData);
            } else if (Array.isArray(sensitiveData)) {
                secureClearArray(sensitiveData);
                sensitiveData = null;
            } else if (typeof sensitiveData === 'object' && sensitiveData !== null) {
                secureClearObject(sensitiveData);
                sensitiveData = null;
            } else {
                sensitiveData = null;
            }
            
            // Additional security: Generate random data to help overwrite memory
            // This is best-effort since we can't control JS garbage collection
            try {
                generateRandomOverwrite(1024); // Generate 1KB of random data
            } catch (error) {
                // Ignore errors in cleanup
            }
        },
        isCleared: () => sensitiveData === null
    };
}

/**
 * Secure timeout that clears sensitive data after specified time
 * @param {any} data - Sensitive data
 * @param {number} timeoutMs - Timeout in milliseconds
 * @returns {Function} Clear function
 */
export function secureTimeout(data, timeoutMs = 30000) { // Default 30 seconds
    const wrapper = createSecureWrapper(data);
    
    const timeoutId = setTimeout(() => {
        wrapper.clear();
    }, timeoutMs);
    
    return () => {
        clearTimeout(timeoutId);
        wrapper.clear();
    };
}

/**
 * Validate that no sensitive data is being logged
 * @param {any} data - Data to check
 * @returns {boolean} True if safe to log
 */
export function isSafeToLog(data) {
    if (typeof data === 'string') {
        // Check for common sensitive patterns
        const sensitivePatterns = [
            /^[A-Za-z0-9+/]{100,}={0,2}$/, // Base64 encoded data (likely keys)
            /^[0-9a-f]{64,}$/i, // Hex encoded data (likely keys)
            /private.*key/i, // Contains "private" and "key"
            /secret/i, // Contains "secret"
            /signature/i // Contains "signature"
        ];
        
        return !sensitivePatterns.some(pattern => pattern.test(data));
    }
    
    if (typeof data === 'object' && data !== null) {
        const jsonString = JSON.stringify(data);
        return isSafeToLog(jsonString);
    }
    
    return true;
}

/**
 * Safe console.log that prevents logging sensitive data
 * @param {string} message - Log message
 * @param {any} data - Data to log (will be filtered)
 */
export function safeLog(message, data = null) {
    if (data !== null && !isSafeToLog(data)) {
    } else {
    }
}

/**
 * Create a secure keypair object with auto-cleanup
 * @param {Object} keypair - Keypair object with public_key and private_key
 * @returns {Object} Secure keypair wrapper
 */
export function createSecureKeypair(keypair) {
    const wrapper = createSecureWrapper(keypair);
    
    return {
        getPublicKey: () => wrapper.get()?.public_key,
        getPrivateKey: () => wrapper.get()?.private_key,
        clear: () => wrapper.clear(),
        isCleared: () => wrapper.isCleared()
    };
}
